import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useChefsLab } from '../../context/ChefsLabContext';
import { supabase } from '../../lib/supabase';
import CardImage from '../CardImage';

// Back icon
const BackIcon = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

// Search icon
const SearchIcon = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

// Close icon
const CloseIcon = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const CardSelector = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { setSelectedCard, setResearchData } = useChefsLab();

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch demo cards from Supabase
  useEffect(() => {
    const fetchDemoCards = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .from('chefs_lab_demo_cards')
          .select('*')
          .order('name');

        if (fetchError) throw fetchError;

        setCards(data || []);
      } catch (err) {
        console.error('Error fetching demo cards:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDemoCards();
  }, []);

  // Filter cards based on search query
  const filteredCards = useMemo(() => {
    if (!searchQuery.trim()) return cards;

    const query = searchQuery.toLowerCase();
    return cards.filter(card =>
      card.name.toLowerCase().includes(query) ||
      card.set_name.toLowerCase().includes(query) ||
      card.number.toLowerCase().includes(query) ||
      (card.variant && card.variant.toLowerCase().includes(query))
    );
  }, [cards, searchQuery]);

  // Handle card selection
  const handleSelectCard = async (card) => {
    // Scroll to top when selecting a card
    window.scrollTo(0, 0);

    setSelectedCard(card);

    // For demo mode, we'll look up research data by card name and set
    // The research data was pre-populated from our test batches
    try {
      // Try multiple lookup strategies
      const { data } = await supabase
        .from('card_research_cache_test')
        .select('*')
        .ilike('card_name', card.name)
        .ilike('set_name', `%${card.set_name}%`)
        .limit(1)
        .maybeSingle();

      if (data) {
        setResearchData(data);
      } else {
        // No cached data - clear research data
        setResearchData(null);
      }
    } catch (err) {
      console.error('Error fetching research data:', err);
      setResearchData(null);
    }
  };

  // Get variant display label
  const getVariantLabel = (variant) => {
    if (!variant) return null;
    const labels = {
      firstEdition: '1st Ed',
      unlimited: 'Unlimited',
      shadowless: 'Shadowless',
      holo: 'Holo',
      reverseHolo: 'Reverse',
      nonHolo: 'Non-Holo',
    };
    return labels[variant] || variant;
  };

  const styles = {
    container: {
      padding: '16px',
      paddingBottom: '100px',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '20px',
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      borderRadius: '12px',
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
      cursor: 'pointer',
      flexShrink: 0,
    },
    headerText: {
      flex: 1,
    },
    title: {
      color: theme.text,
      fontSize: '24px',
      fontWeight: 700,
      margin: '0 0 4px 0',
    },
    subtitle: {
      color: theme.textMuted,
      fontSize: '14px',
      margin: 0,
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: theme.bgInput,
      borderRadius: '12px',
      border: `1px solid ${theme.border}`,
      padding: '12px 16px',
      marginBottom: '20px',
      gap: '12px',
    },
    searchInput: {
      flex: 1,
      background: 'none',
      border: 'none',
      outline: 'none',
      color: theme.text,
      fontSize: '16px',
    },
    clearButton: {
      background: 'none',
      border: 'none',
      padding: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: '12px',
    },
    card: {
      backgroundColor: theme.bgCard,
      borderRadius: '12px',
      border: `1px solid ${theme.border}`,
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    cardHover: {
      borderColor: theme.accent,
      transform: 'scale(1.02)',
    },
    cardImage: {
      width: '100%',
      aspectRatio: '2.5/3.5',
      objectFit: 'cover',
      backgroundColor: theme.bgInput,
    },
    cardImagePlaceholder: {
      width: '100%',
      aspectRatio: '2.5/3.5',
      backgroundColor: theme.bgInput,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.textMuted,
      fontSize: '12px',
    },
    cardInfo: {
      padding: '10px',
    },
    cardName: {
      color: theme.text,
      fontSize: '13px',
      fontWeight: 600,
      margin: '0 0 4px 0',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    cardSet: {
      color: theme.textMuted,
      fontSize: '11px',
      margin: '0 0 4px 0',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    cardMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      flexWrap: 'wrap',
    },
    cardNumber: {
      color: theme.textSecondary,
      fontSize: '11px',
      backgroundColor: theme.bgInput,
      padding: '2px 6px',
      borderRadius: '4px',
    },
    variantBadge: {
      fontSize: '10px',
      fontWeight: 600,
      padding: '2px 6px',
      borderRadius: '4px',
      backgroundColor: theme.accentBg,
      color: theme.accent,
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
    },
    spinner: {
      width: '32px',
      height: '32px',
      border: `3px solid ${theme.border}`,
      borderTopColor: theme.accent,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },
    errorContainer: {
      textAlign: 'center',
      padding: '40px 20px',
      color: theme.error,
    },
    emptyContainer: {
      textAlign: 'center',
      padding: '40px 20px',
      color: theme.textMuted,
    },
    count: {
      color: theme.textMuted,
      fontSize: '13px',
      marginBottom: '12px',
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <p>Error loading demo cards</p>
          <p style={{ fontSize: '12px' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <BackIcon color={theme.text} />
        </button>
        <div style={styles.headerText}>
          <h1 style={styles.title}>Chef's Lab DEMO</h1>
          <p style={styles.subtitle}>Select a card to view pricing data</p>
        </div>
      </div>

      <div style={styles.searchContainer}>
        <SearchIcon color={theme.textMuted} />
        <input
          type="text"
          placeholder="Search by name, set, or number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
        {searchQuery && (
          <button
            style={styles.clearButton}
            onClick={() => setSearchQuery('')}
          >
            <CloseIcon color={theme.textMuted} />
          </button>
        )}
      </div>

      <div style={styles.count}>
        {filteredCards.length} of {cards.length} cards
      </div>

      {filteredCards.length === 0 ? (
        <div style={styles.emptyContainer}>
          <p>No cards found matching "{searchQuery}"</p>
        </div>
      ) : (
        <div style={styles.grid}>
          <AnimatePresence>
            {filteredCards.map((card) => (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                whileHover={{ scale: 1.02, borderColor: theme.accent }}
                style={styles.card}
                onClick={() => handleSelectCard(card)}
              >
                <CardImage
                  digitalImage={card.digital_image_url}
                  name={card.name}
                  number={card.number}
                  setName={card.set_name}
                  theme={theme}
                  imgStyle={styles.cardImage}
                  loading="lazy"
                />
                <div style={styles.cardInfo}>
                  <p style={styles.cardName}>{card.name}</p>
                  <p style={styles.cardSet}>{card.set_name}</p>
                  <div style={styles.cardMeta}>
                    <span style={styles.cardNumber}>{card.number}</span>
                    {card.variant && (
                      <span style={styles.variantBadge}>
                        {getVariantLabel(card.variant)}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default CardSelector;
